import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'outline' | 'ghost' | 'dark';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-primary-600 text-background-50 hover:bg-primary-700 active:bg-primary-800 border border-transparent',
  outline:
    'bg-background-50 text-foreground-900 border border-background-300 hover:border-primary-400 hover:text-primary-700',
  ghost: 'bg-transparent text-foreground-700 hover:bg-background-200 border border-transparent',
  dark: 'bg-foreground-900 text-background-50 hover:bg-foreground-950 border border-transparent',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm rounded-xl gap-2',
  md: 'h-11 px-5 text-sm rounded-xl gap-2',
  lg: 'h-12 px-6 text-[15px] rounded-2xl gap-2.5',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  fullWidth = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center whitespace-nowrap font-label font-semibold transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 ${variants[variant]} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {loading ? (
        <span className="flex items-center justify-center w-4 h-4">
          <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
        </span>
      ) : (
        icon
      )}
      {children}
    </button>
  );
}